import { useState } from "react";
import { Crop, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { CornerEditor } from "./CornerEditor";
import { MODES, toFile } from "./CameraCapture";
import { detectQuad, warp, outSize } from "../lib/perspective";

// True when any edge of the found card leans more than 3 degrees off straight.
const tilted = (q) => q.some((p, i) => {
  const n = q[(i + 1) % 4], a = Math.abs((Math.atan2(n[1] - p[1], n[0] - p[0]) * 180) / Math.PI) % 90;
  return Math.min(a, 90 - a) > 3;
});

const toCanvas = async (file, long = 1600) => {
  const img = await createImageBitmap(file);
  const k = Math.min(1, long / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return c;
};

// Gallery photos of cards/cheques: offers the corner step only when the card looks tilted; otherwise the file passes through.
export const useStraighten = () => {
  const [job, setJob] = useState(null);
  const run = async (file, mode, done) => {
    if (!file || !/^image\/(jpe?g|png)$/.test(file.type)) return done(file);
    let c = null, q = null;
    try { c = await toCanvas(file); q = detectQuad(c); } catch { return done(file); }
    if (!q || !tilted(q)) return done(file);
    setJob({ canvas: c, url: c.toDataURL("image/jpeg", 0.85), quad: q, mode, file, done });
  };
  const finish = async (flat) => {
    const { canvas, quad, mode, file, done } = job;
    setJob(null);
    if (!flat) return done(file);
    const cfg = MODES[mode];
    const [w, h] = outSize(quad, cfg.out, cfg.free ? null : cfg.ratio);
    done((await toFile(warp(canvas, quad, w, h), mode)) || file);
  };
  const el = job && (
    <Dialog open onOpenChange={() => finish(false)}>
      <DialogContent className="bg-[#121824] border-[#1F2A3E] max-w-md" data-testid="straighten-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">Straighten this photo?</DialogTitle>
          <p className="text-xs text-slate-400">The card looks tilted. Drag any green dot onto the card's corner if needed, then tap Straighten.</p></DialogHeader>
        <CornerEditor url={job.url} width={job.canvas.width} height={job.canvas.height} quad={job.quad} setQuad={(quad) => setJob((j) => ({ ...j, quad }))} />
        <div className="flex flex-wrap gap-2 justify-center">
          <Button onClick={() => finish(true)} className="bg-[#00E599] text-[#0B1017] font-semibold" data-testid="straighten-apply-btn"><Crop size={14} className="mr-1.5" />Straighten</Button>
          <Button variant="outline" onClick={() => finish(false)} className="border-[#1F2A3E]" data-testid="straighten-skip-btn"><Check size={14} className="mr-1.5" />Use as it is</Button>
        </div>
      </DialogContent>
    </Dialog>);
  return [run, el];
};
